import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { dedupedRequest } from '../../utils/axios';
import LawModal from './modals/LawModal';
import EditIcon from '../icons/EditIcon';
import DeleteIcon from '../icons/DeleteIcon';
import Plus from '../icons/Plus';
import Warn from '../icons/Warn';
// import Box from '../icons/Box';
// import BoxChecked from '../icons/BoxChecked';
// import { toDE } from '../../utils/utils'
// import ClientsPanel from './ClientsPanel';

export interface LegalArea {
  id: number;
  color: string;
  name: string;
}

export interface Cluster {
  id: number;
  color: string;
  name: string;
}

export interface ClientLawPart {
  id: number;
  law_part_id: number;
  client_id: number;
  client: string;
  cluster_ids: number[] | null;
  version: number;
  comments: string;
  notes: string;
}

export interface LawPart {
  id: number;
  law_id: number;
  law_part_id: number;
  paragraphs: string;
  regulation: string;
  version: number;
  comments?: string;
  client_versions: ClientLawPart[];
}

export interface Law {
  id: number;
  law_id: number;
  name: string;
  abbr: string;
  last_change_date: string;
  last_change: string;
  last_check_date?: string;
  link1?: string;
  link2?: string;
  version: number;
  current?: boolean;
  parts: LawPart[];
  legal_areas: LegalArea[];
  amendment_date?: string;
  amendment_comment?: string;
}

const LawManagementPanel = () => {
  const [laws, setLaws] = useState<Law[]>([]);
  const [expandedLaws, setExpandedLaws] = useState<Set<number>>(new Set());
  const [activeLaw, setActiveLaw] = useState<any | null>(null);
  const [selectedClient, setSelectedClient] = useState<number | null>(null);
  const [targetClient, setTargetClient] = useState<number | null>(null);
  const [selectedLegalArea, setSelectedLegalArea] = useState<number | null>(null);
  const [selectedCluster, setSelectedCluster] = useState<number | null>(null);
  const [targetClientError, setTargetClientError] = useState<string>('');
  const [showTargetClientError, setShowTargetClientError] = useState(false);
  const [all_clients, setClients] = useState<any[]>([]);
  const [all_legal_areas, setLegalAreas] = useState<any[]>([]);
  const [all_clusters, setClusters] = useState<any[]>([]);
  const [filter, setFilter] = useState('');
  const [lawExpansion, setLawExpansion] = useState(true);
  const [clientExpansion, setClientExpansion] = useState<boolean | undefined>(true);
  const [sortKey, setSortKey] = useState<keyof Law>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [showLastCheck, setShowLastCheck] = useState(false);
  

  useEffect(() => {
    loadLaws();
    loadClients();
    loadLegalAreas();
    loadClusters();
  }, []);

  const handleSort = (key: keyof Law) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const loadLaws = async () => {
    dedupedRequest({ method: 'GET', url: '/admin/laws/tree' }, { useCache: false })
      .then(res => setLaws(res.data))
      .catch(err => console.error("Failed to load laws tree", err));
  };

  const loadClients = async () => {
    dedupedRequest({ method: 'GET', url: '/admin/clients' }, { useCache: true })
      .then(res => setClients(res.data))
      .catch(err => console.error("Failed to load clients", err));
  };

  const loadLegalAreas = async () => {
    dedupedRequest({ method: 'GET', url: '/admin/legal_areas' }, { useCache: false })
      .then(res => setLegalAreas(res.data))
      .catch(err => console.error("Failed to load legal_areas", err));
  };

  const loadClusters = async () => {
    dedupedRequest({ method: 'GET', url: '/admin/clusters' }, { useCache: true })
      .then(res => setClusters(res.data))
      .catch(err => console.error("Failed to load clusters", err));
  };

  const toggleLaw = (lawId: number) => {
    setExpandedLaws(prev => {
      const next = new Set(prev);
      next.has(lawId) ? next.delete(lawId) : next.add(lawId);
      return next;
    });
  };

  const addClientToLawPartSelection = (lawPart: LawPart) => {
    const lawPartId = lawPart.law_part_id;
    console.log('Selected client:', targetClient);
    console.log('LawPartId:', lawPartId);
    if (targetClient == null) {
      setTargetClientError('Bitte wählen Sie einen Zielkunden aus.');
      setShowTargetClientError(true);
      return false;
    }
    setTargetClientError('');
    if (lawPart.client_versions.some(clp => clp.client_id === targetClient)) {
      console.error("Client already assigned to this law part");
      return false;
    }
    console.log(`Adding client ${targetClient} to law part ${lawPartId}`);
    dedupedRequest({ method: 'POST', url: `/admin/client_law_parts/${lawPartId}/client/${targetClient}` }, { useCache: false })
      .then(res => {
        console.log("Added client to law part:", res.data);
        loadLaws(); // Refresh the laws list to show the updated client assignment
      })
      .catch(err => console.error("Failed to add client to law part", err));
    return true;
  };

  const DeleteClientLawPart = ({ clp }: { clp: ClientLawPart }) => {
    if (!window.confirm("Diese Zuordnung wirklich löschen?")) return;
    console.log('Deleting ClientLawPart with IDs:', clp.id);
    dedupedRequest({ method: 'DELETE', url: `/admin/client_law_parts/${clp.id}` }, { useCache: false })
      .then(res => {
        console.log("Deleted ClientLawPart:", res.data);
        loadLaws(); // Refresh the laws list to show the updated client assignment
      })
      .catch(err => console.error("Failed to delete ClientLawPart", err));
    return true;
  };

  const DeleteLawPart = ({ lawPartId }: { lawPartId: number }) => {
    if (!window.confirm("Diesen Vorschriftenteil wirklich löschen?")) return;
    console.log('Deleting LawPart with IDs:', lawPartId);
    dedupedRequest({ method: 'DELETE', url: `/admin/law_parts/${lawPartId}` }, { useCache: false })
      .then(res => {
        console.log("Deleted LawPart:", res.data);
        loadLaws(); // Refresh the laws list to show the updated client assignment
      })
      .catch(err => console.error("Failed to delete LawPart", err));
    return true;
  };

  // function log_item(item: any) {
  //   console.log(item);
  //   return true;
  // };

  function delete_law(lawId: number) {
    if (!window.confirm("Diese Vorschrift wirklich löschen?")) return;
    console.log('Deleting law with IDs:', lawId);
    dedupedRequest({ method: 'DELETE', url: `/admin/laws/${lawId}` }, { useCache: false })
      // .then(res => setLaws(res.data))
      .catch(err => console.error("Failed to load laws tree", err));
    removeLawFromSelection(lawId);
    return true;
  };

  function LastChangeDateItem({last_change_date}: { last_change_date: string }) {
    if (last_change_date === null || last_change_date == '') return '';
    const date = new Date(last_change_date);
    return date.toLocaleDateString('de-DE', {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  function LastCheckDateItem({last_check_date}: { last_check_date: string | undefined }) {
    if (last_check_date === null || last_check_date == '') return '';
    const date = new Date(last_check_date || '');
    return date.toLocaleDateString('de-DE', {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const removeLawFromSelection = (lawId: number) => {
    const next = laws.filter((law) => law.law_id !== lawId);
    setLaws(next);
  }

  const handleLawExpansion=()=>{
    setLawExpansion(!lawExpansion);
  };

  const handleClientExpansion=()=>{
    setClientExpansion(!clientExpansion);
  };

  const handleShowLastCheck=()=>{
    setShowLastCheck(!showLastCheck);
  };

  const handleAddLaw = () => {
    const newId = -1;
    const newLaw: Law = {
      id: -1,
      law_id: -1,
      version: 1,
      name: '',
      abbr: '',
      last_change_date: '',
      last_change: '',
      last_check_date: '',
      link1: '',
      link2: '',
      parts: [],
      legal_areas: [],
    };
    const updatedLaws = [...laws, newLaw];
    setLaws(updatedLaws);
    setActiveLaw({ law: newLaw, LawId: newId });
  };

  const filteredLaws = laws.filter((law: Law) => {
    const matchesLegalArea = selectedLegalArea == null || law.legal_areas?.some(area => area.id === selectedLegalArea);
    const matchesClient = selectedClient == null || law.parts?.some((part: LawPart) =>
      part.client_versions?.some(clp => clp.client_id === selectedClient)
    );
    const matchesSearch = 
      (law.name != null && law.name.toLowerCase().includes(filter.toLowerCase())) ||
      (law.abbr != null && law.abbr.toLowerCase().includes(filter.toLowerCase())) ||
      law.legal_areas?.some(area => area.name != null && area.name.toLowerCase().includes(filter.toLowerCase()))
      ;
    return matchesClient && matchesLegalArea && matchesSearch;
  })
  .sort((a, b) => {
    const aVal = a[sortKey]?.toString().toLowerCase?.() || '';
    const bVal = b[sortKey]?.toString().toLowerCase?.() || '';
    return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

{/* 
//#region HTML
*/}
  return (
    <div>
      <h3>Vorschriften Management</h3>
      <div className="mb-3 d-flex gap-3">
        <Form.Select value={selectedClient ?? ''} onChange={(e) => setSelectedClient(e.target.value ? parseInt(e.target.value) : null)}>
          <option value="">Alle Kunden</option>
          {all_clients.map(client => (
            <option key={client.id} value={client.id}>{client.name}</option>
          ))}
        </Form.Select>

        <Form.Select value={selectedLegalArea ?? ''} onChange={(e) => setSelectedLegalArea(e.target.value ? parseInt(e.target.value) : null)}>
          <option value="">Alle Rechtsgebiete</option>
          {all_legal_areas.map(area => (
            <option key={area.id} value={area.id}>{area.name}</option>
          ))}
        </Form.Select>

        <Form.Select value={selectedCluster ?? ''} onChange={(e) => setSelectedCluster(e.target.value ? parseInt(e.target.value) : null)}>
          <option value="">Alle Cluster</option>
          {all_clusters.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Form.Select>
      </div>
      <div className="mb-3 d-flex gap-3">
        <Form.Control
          type="search"
          placeholder="Filtern nach Verordnung und Abkürzung"
          className="mb-3"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className='text-muted'>{ filteredLaws.length === 0 ? 'Keine' : filteredLaws.length } Vorschrift{ filteredLaws.length === 1 ? '' : 'en' } gefunden</div>
      </div>
      <div className="mb-3 d-flex gap-3 align-items-center">
        <Form.Switch
          id='lawExpansionSwitch'
          label="Alle Verordnungen aufklappen"
          defaultChecked={lawExpansion}
          onChange={handleLawExpansion}
        />
        <Form.Switch
          id='clientExpansionSwitch'
          key={'client_switch'}
          label="Alle Kunden aufklappen"
          defaultChecked={clientExpansion}
          onChange={handleClientExpansion}
        />
        <Button variant="outline-primary" size="sm" onClick={handleAddLaw}>
          + Neu Vorschrift hinzufügen
        </Button>

        <div className="d-flex flex-column">
          <div className="d-flex align-items-center gap-2">
            Zielkunde:&nbsp;
            <Form.Select
              className='form-select-auto'
              id='target-client'
              value={targetClient ?? ''}
              onChange={(e) => {
                const clientId = e.target.value ? parseInt(e.target.value) : null;
                setTargetClient(clientId);
                if (clientId !== null && targetClientError) setTargetClientError('');
              }}
            >
              <option value="">Kunde auswählen</option>
              {all_clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </Form.Select>
          </div>
          {targetClientError && (
            <div className="text-danger small mt-1" role="alert">
              {targetClientError}
            </div>
          )}
        </div>
        <Form.Switch
          id='showLastCheckSwitch'
          key={'check_switch'}
          label="Überprüfung anzeigen"
          defaultChecked={showLastCheck}
          onChange={handleShowLastCheck}
        />

        {/* <EditIcon /> = Vorschrift bearbeiten,&nbsp; <DeleteIcon /> = Vorschrift löschen <Plus /> = Kunde zu Paragraf hinzufügen */}
      </div>
      <table className="table table-sm">
        <thead>
          <tr className='law-main'>
            <th>ID</th>
            <th colSpan={1} onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
              Vorschrift&nbsp;{sortKey === 'name' && (sortAsc ? '↑' : '↓')}</th>
            <th onClick={() => handleSort('abbr')} style={{ cursor: 'pointer' }}>
              Abkürzung&nbsp;Vorschrift&nbsp;{sortKey === 'abbr' && (sortAsc ? '↑' : '↓')}</th>
            <th>Rechtsgebiet</th>
            <th onClick={() => handleSort('last_change_date')} style={{ cursor: 'pointer' }}>Datum letzte Änderung</th>
            {showLastCheck && <th onClick={() => handleSort('last_check_date')} style={{ cursor: 'pointer' }}>Datum letzte Überprüfung</th>}
            <th className='actions'></th>
          </tr>
        </thead>
        <tbody>
          {filteredLaws.map((law: Law) => (
            <React.Fragment key={law.id}>
              <tr className={expandedLaws.has(law.law_id) ? 'c' : 'law-main'}>
                <td colSpan={1} className='law_id' onClick={() => toggleLaw(law.law_id)}>{law.law_id}</td>
                <td colSpan={1} className='law_name' onClick={() => toggleLaw(law.law_id)} title={!law.current ? "Verordnung außer Kraft" : undefined}>{law.current ? law.name : <><Warn/> {law.name}</>}</td>
                <td onClick={() => toggleLaw(law.law_id)}>{law.abbr}</td>
                <td onClick={() => toggleLaw(law.law_id)}>{law.legal_areas.map((area) => (
                    <div className='legal-area' key={area.id} style={{backgroundColor: area.color}}>{area.name}</div>
                    ))}</td>
                <td onClick={() => toggleLaw(law.law_id)}><LastChangeDateItem last_change_date={law.last_change_date} /></td>
                {showLastCheck && <td onClick={() => toggleLaw(law.law_id)}><LastCheckDateItem last_check_date={law.last_check_date} /></td>}
                <td className='actions'>
                  <span onClick={() => delete_law(law.law_id)}><DeleteIcon/></span>
                  <span onClick={() => setActiveLaw({ law, LawId: law.law_id })}><EditIcon/></span>
                </td>
              </tr>

              {(lawExpansion || expandedLaws.has(law.law_id)) && (
                <tr className='law-main'>
                  <td colSpan={8}>
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr className='detail-header'>
                          <th colSpan={2} className='indent'>Paragraf</th>
                          <th>Inhalt</th>
                          <th>Anmerkungen</th>
                          <th className='actions'></th>
                          <th className='actions'></th>
                        </tr>
                      </thead>
                      <tbody>
                        { 
                        law.parts?.map((part: LawPart) => (
                          <React.Fragment key={part.id}>
                            <tr>
                              <td className='law-selected'>
                                <span onClick={() => addClientToLawPartSelection(part)}>
                                  <Plus />
                                </span>
                              </td>
                              <td className='indent'>{part.paragraphs}</td>
                              <td>{part.regulation}</td>
                              <td>{part.comments}</td>
                              {/* <td className='actions'></td> */}
                              <td className='actions'><span onClick={() => DeleteLawPart({lawPartId: part.law_part_id})}><DeleteIcon/></span></td>
                            </tr>
                            {clientExpansion && part.client_versions?.map((clp: ClientLawPart) => (
                              <tr className="table-light" key={clp.id}>
                                <td colSpan={2} className='indent2'>{clp.client}</td>
                                <td>
                                  {clp.cluster_ids?.map((c) => {
                                    const cluster = all_clusters.find((ac) => ac.id === c);
                                    if (!cluster) return <span key={c}>Unbekannt ({c})</span>;
                                    return (
                                      <div 
                                        key={cluster.id}
                                        className={`cluster cluster-${c}`}
                                        style={{backgroundColor: cluster.color}}
                                      >
                                      {cluster.name}
                                    </div>
                                  );})}
                                </td>
                                <td>{clp.notes}</td>
                                <td>{clp.comments}</td>
                                <td className='actions'>
                                  <Button
                                    size="sm"
                                    className='action-btn'
                                    onClick={() =>
                                      DeleteClientLawPart({
                                        clp
                                      })
                                    }
                                    // onClick={() =>
                                    //   console.log({
                                    //     clp,
                                    //   })
                                    // }
                                  >
                                    <DeleteIcon />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <Modal show={showTargetClientError} onHide={() => setShowTargetClientError(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Zielkunde erforderlich</Modal.Title>
        </Modal.Header>
        <Modal.Body>{targetClientError}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowTargetClientError(false)}>
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>

      {activeLaw && (
        <LawModal
          show={true}
          {...activeLaw}
          onClose={() => setActiveLaw(null)}
          onSave={loadLaws}
        />
      )}
    </div>
  );
};

export default LawManagementPanel;
